/**
 * Date-only helpers for fields the API serializes as DateTime but that
 * semantically represent a calendar date (start date, finish date, etc.).
 *
 * Background: <input type="date"> values are calendar dates with no time of
 * day, but the API returns them as DateTime ("2026-05-29T00:00:00.000Z").
 * Passing that straight into `new Date()` and rendering in a non-UTC locale
 * shifts the day backward (May 29 UTC -> May 28 EDT), so a field picked as
 * 5/29 displays as 5/28. Always route date-only fields through these helpers
 * so display and day-math stay aligned with what the user actually picked.
 *
 * Do NOT use these helpers for timestamps (createdAt, updatedAt, scheduled
 * datetimes, etc.) — those represent a real moment in time and should keep
 * their existing timezone-aware behavior.
 */

/**
 * Parse the YYYY-MM-DD portion of an ISO date as a *local* calendar date,
 * deliberately discarding any time-of-day or timezone information.
 */
export function parseLocalDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Whether a `YYYY-MM-DD` string names a day that exists.
 *
 * ⚠️ Not `parseLocalDate`: that one hands `new Date(y, m, d)` the parts, and the Date
 * constructor rolls 2026-02-30 over to March 2nd instead of refusing it, so it cannot
 * tell a real day from one that merely has the right shape. Checked by arithmetic, and
 * not through `Date.UTC`, which maps years 0-99 into the 1900s.
 */
export function isRealYmd(ymd: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  return day <= days;
}

/** Format an ISO date-only string as "May 21, 2026" — returns "—" for null/empty. */
export function fmtDate(iso: string | null | undefined): string {
  const d = parseLocalDate(iso);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format an ISO *timestamp* (a real moment — createdAt, decidedAt, audit events)
 * in the viewer's local zone as "May 21, 2026, 2:15 PM". Returns "—" for null.
 * Unlike fmtDate this keeps the time of day and is timezone-aware (per this
 * file's guidance: timestamps are real moments, not calendar dates).
 */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Format an ISO *timestamp* as a date only — "May 21, 2026" — in the viewer's
 * local zone. Returns "—" for null.
 *
 * ⚠️ Use this, NOT `fmtDate`, for a real moment (createdAt, updated_at, audit
 * events) that you want to show without a time of day. `fmtDate` slices the
 * date portion off the string and rebuilds it as a local calendar date, so a
 * UTC timestamp from 8pm Eastern — stored on the next UTC day — renders as
 * TOMORROW. That is correct for a DATE column and wrong for a DATETIME one.
 * `fmtDateTime` is the same thing with the clock time kept.
 */
export function fmtInstantDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Trim an ISO datetime down to YYYY-MM-DD so it fits an <input type="date">.
 *
 * Accepts a `Date` as well as a string. It used to call `.slice` straight on its
 * argument, so a `Date` threw a TypeError and took down whatever was mid-render
 * or mid-export — a hazard for any caller handing over a value straight out of
 * the driver rather than off the wire. `String(d).slice(0, 10)` is not the fix
 * either: that yields "Tue Jun 30".
 *
 * The UTC slice is deliberate. These are calendar dates, and mssql returns a
 * DATE column as a Date at UTC midnight; reading it with the local getters
 * would shift it a day for anyone west of UTC.
 */
export function toDateInputValue(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  if (iso instanceof Date) return Number.isNaN(iso.getTime()) ? '' : iso.toISOString().slice(0, 10);
  return String(iso).slice(0, 10);
}

/**
 * Plausible calendar-date bounds for <input type="date"> min/max. Guards
 * against fat-fingered 2-digit years — a browser zero-pads "26" to the year
 * 0026, which would store as year 26 and render as "1926". No E&W date
 * legitimately falls outside 2000-2100. The API enforces the same range.
 */
export const DATE_INPUT_MIN = '2000-01-01';
export const DATE_INPUT_MAX = '2100-12-31';

/**
 * A `Date` reduced to the YYYY-MM-DD it represents *in the viewer's zone*.
 *
 * The counterpart to `toDateInputValue` above, and the choice between them is
 * about what the Date MEANS, not about preference:
 *   - a stored calendar date off the wire (UTC midnight)  -> `toDateInputValue`
 *   - a real moment, i.e. anything derived from `new Date()` -> this
 *
 * Getting that backwards is why a run at 8:30pm Eastern reported itself as the
 * next day: `new Date().toISOString()` is the UTC instant, and Eastern is
 * already past UTC midnight all evening.
 */
export function toLocalDateInputValue(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Today's *local* calendar date as YYYY-MM-DD, suitable for an
 * <input type="date"> value. Built from local components — do NOT use
 * `toDateInputValue(new Date().toISOString())`, which is the UTC date and so
 * runs a day AHEAD of a negative-offset viewer all evening (Eastern after 8pm).
 */
export function todayInputValue(): string {
  return toLocalDateInputValue(new Date());
}

/**
 * True when an ISO date falls within an inclusive YYYY-MM-DD range. An empty
 * bound leaves that side open. Comparison is date-only string comparison (safe
 * for ISO dates). A null/empty date is considered out of range whenever either
 * bound is set — it can't be placed in a dated window.
 */
export function isDateInRange(
  iso: string | null | undefined,
  from: string,
  to: string,
): boolean {
  if (!from && !to) return true;
  const s = iso ? iso.slice(0, 10) : null;
  if (!s) return false;
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
}

/** Whole-day delta from today (local midnight) to the given ISO date. */
export function daysFromToday(iso: string | null | undefined): number | null {
  const target = parseLocalDate(iso);
  if (!target) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}
